import { forwardRef } from "react";

import { Box, SxProps, Theme, useTheme } from "@mui/material";

import { InvalidThemeErrorMessage } from "../InvalidThemeErrorMessage";

import { FormButton, FormButtonProps } from "./FormButton";

import { mergeSx } from "@/helpers/mergeSx";

export type RippleFormButtonProps = Omit<FormButtonProps, "variant" | "backgroundContent"> & {
  rippleOrigin?: "top-right" | "bottom-left" | "right" | "left";
};

export const RippleFormButton = forwardRef<HTMLButtonElement, RippleFormButtonProps>(
  ({ iconButton, rippleOrigin = "top-right", sx, ...rest }, ref) => {
    const theme = useTheme();

    if (!theme.palette.forms || !theme.palette.formsComponents?.button) {
      return <InvalidThemeErrorMessage />;
    }

    const styles: SxProps<Theme> = mergeSx(
      {
        position: "relative",
        overflow: "hidden",
        backgroundColor: (theme) => theme.palette.formsComponents.button.tonal.hoverBackgroundColor,
        "&:hover": {
          /* Leave this empty to remove hover effect of base button */
        },
        "& .button-ripple": {
          position: "absolute",
          ...(rippleOrigin === "top-right" && {
            top: 0,
            right: 10,
            translate: "50% -50%",
          }),
          ...(rippleOrigin === "bottom-left" && {
            bottom: 0,
            left: 10,
            translate: "-50% 50%",
          }),
          ...(rippleOrigin === "right" && {
            top: "50%",
            right: 10,
            translate: "50% -50%",
          }),
          ...(rippleOrigin === "left" && {
            top: "50%",
            left: 10,
            translate: "-50% -50%",
          }),
          zIndex: 0,
          width: iconButton
            ? "283%" // The radius of the blowup icon must exceed the length of the diagonal of the button
            : "200%",
          aspectRatio: "1/1",

          borderRadius: "50%",
          transition: "all ease-in-out",
          transitionDuration: "200ms",
          backgroundColor: (theme) => theme.palette.formsComponents.button.tonal.backgroundColor,
        },
        "&:hover .button-ripple": {
          width: 0,
          opacity: 0.5,
        },
      },
      sx
    );

    const blowupIcon = <Box className="button-ripple" />;

    return (
      <FormButton
        variant="tonal"
        sx={styles}
        iconButton={iconButton}
        backgroundContent={blowupIcon}
        ref={ref}
        {...rest}
      />
    );
  }
);

RippleFormButton.displayName = "RippleFormButton";
